import { useFormContext } from 'react-hook-form'

import { type Allowances } from 'components/quoting/quotes/flat_allowances/FlatAllowanceExplorer'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { parseNumber } from 'pages/employer_onboarding/utils'
import { NumberMask, TextInput } from 'slab'

interface Props {
  formIndex: number;
  readOnly?: boolean;
  loading?: boolean;
}

export function AllowanceTextInput ({
  formIndex,
  readOnly,
  loading,
}: Props) {
  const { register, formState: { errors } } = useFormContext<Allowances>()

  return (
    <div className='flex flex-col gap-2 w-full'>
      <div className='w-full'>
        {loading
          ? (
            <SkeletonItem heightClass='h-10' />
            )
          : (
            <div className='flex-1 w-full'>
              <NumberMask>
                <TextInput
                  className='rounded-full'
                  label='Employee allowance'
                  type='number'
                  min={0}
                  readOnly={readOnly || loading}
                  {...register(`allowances.${formIndex}.flat_config.employee_amount`, {
                    required: 'Employee allowance is required',
                    validate: (value) => {
                      if (value === undefined) {
                        return 'Employee allowance is required'
                      }
                      if (value <= 0) {
                        return 'Employee allowance must be greater than 0'
                      }
                      return true
                    },
                    setValueAs: (value) => parseNumber(value),
                  })}
                  error={errors.allowances?.[formIndex]?.flat_config?.employee_amount?.message?.toString()}
                />
              </NumberMask>
            </div>
            )}
      </div>
      <div className='flex gap-2 w-full'>
        {loading
          ? (
            <SkeletonItem heightClass='h-10 w-full' />
            )
          : (
            <div className='flex-1 w-full'>
              <NumberMask>
                <TextInput
                  className='rounded-full'
                  label='First dependent allowance'
                  type='number'
                  min={0}
                  readOnly={readOnly || loading}
                  {...register(`allowances.${formIndex}.flat_config.spouse_amount`, {
                    required: 'First dependent allowance is required',
                    validate: (value) => {
                      if (value === undefined) {
                        return 'First dependent allowance is required'
                      }
                      if (value < 0) {
                        return 'First dependent allowance must be greater than or equal to 0'
                      }
                      return true
                    },
                    setValueAs: (value) => parseNumber(value),
                  })}
                  error={errors.allowances?.[formIndex]?.flat_config?.spouse_amount?.message?.toString()}
                />
              </NumberMask>
            </div>
            )}
        {loading
          ? (
            <SkeletonItem heightClass='h-10 w-full' />
            )
          : (
            <div className='flex-1 w-full'>
              <NumberMask>
                <TextInput
                  className='rounded-full'
                  label='Additional dependent(s) allowance'
                  type='number'
                  min={0}
                  readOnly={readOnly || loading}
                  {...register(`allowances.${formIndex}.flat_config.dependent_amount`, {
                    required: 'Additional dependent(s) allowance is required',
                    validate: (value) => {
                      if (value === undefined) {
                        return 'Additional dependent(s) allowance is required'
                      }
                      if (value < 0) {
                        return 'Additional dependent(s) allowance must be greater than or equal to 0'
                      }
                      return true
                    },
                    setValueAs: (value) => parseNumber(value),
                  })}
                  error={errors.allowances?.[formIndex]?.flat_config?.dependent_amount?.message?.toString()}
                />
              </NumberMask>
            </div>
            )}
      </div>
    </div>
  )
}
